import SessionAvatar from 'components/common/SessionAvatar';
import {
  ChargingHeaderTexts,
  CollapseButton,
  ListButton,
  LoadingSpinner,
  TerminateButton,
} from 'components/ongoingParking/OngoingParkingComponents';
import OngoingSessionHeaderContainer from 'components/ongoingParking/OngoingSessionHeaderContainer';
import {
  HeaderContainer,
  OngoingParkingHeaderWrapper,
} from 'components/ongoingParking/styles';
import useOngoingParkingSession from 'hooks/useOngoingParkingSession';
import useOngoingSessionsSheet from 'hooks/useOngoingSessionsSheet';
import useVehicle from 'hooks/useVehicle';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Animated from 'react-native-reanimated';
import { useParkingStore } from 'stores/parkingStore';
import { ChargingSession, ChargingStatus } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';
import { useUiStore } from 'stores/uiStore';

type ChargingHeaderProps = {
  session: ChargingSession;
};

const getChargingModalTitle = (
  status: ChargingStatus,
  sheetExpanded: boolean
) => {
  if (status === 'IN_PROGRESS') {
    return sheetExpanded
      ? 'evCharging.modal.titleSheetExpanded'
      : 'evCharging.modal.title';
  }
  if (status === 'START_IN_QUEUE') {
    return 'evCharging.modal.startingTitle';
  }
  return 'evCharging.modal.stoppingTitle';
};

const OngoingChargingHeader: React.FC<ChargingHeaderProps> = ({ session }) => {
  const { t } = useTranslation();
  const licensePlate = session?.licensePlate || '';
  const { status, chargingSessionId } = session || {};
  const chargingStarting = status === 'START_IN_QUEUE';
  const chargingOngoing = status === 'IN_PROGRESS';
  const chargingStopping = status === 'STOP_IN_QUEUE';
  const { sessionId: ongoingParkingSessionId } = useParkingStore(
    (state) => state.ongoingParkingSession
  );
  const ongoingChargingSheetExp =
    useParkingStore((state) => state.selectedOngoingSession) === 'CHARGING';
  const titleText = t(
    getChargingModalTitle(status || 'IN_PROGRESS', ongoingChargingSheetExp)
  );
  const { label } = useVehicle(licensePlate);
  const { allParkingSessions } = useOngoingParkingSession();
  const [isMultipleSession, setIsMultipleSession] = useState(false);
  const { collapsedSheetContainerStyle, expandedSheetContainerStyle } =
    useOngoingSessionsSheet();

  useEffect(() => {
    setIsMultipleSession(
      !!ongoingParkingSessionId ||
        !!(allParkingSessions && allParkingSessions.length > 1)
    );
  }, [ongoingParkingSessionId, allParkingSessions]);

  if (!chargingSessionId) {
    return <></>;
  }

  return (
    <HeaderContainer>
      <OngoingSessionHeaderContainer
        type="CHARGING"
        testID="ongoing-charging-container">
        <OngoingParkingHeaderWrapper mr={20} testID="vehicle-avatar-container">
          <SessionAvatar session={session} />
        </OngoingParkingHeaderWrapper>
        <OngoingParkingHeaderWrapper mr={20} grow>
          <ChargingHeaderTexts title={titleText} vehicleLabel={label} />
        </OngoingParkingHeaderWrapper>
        <Animated.View
          style={expandedSheetContainerStyle}
          testID="ongoing-charging-header-expanded">
          <>
            {isMultipleSession && (
              <OngoingParkingHeaderWrapper mr={20}>
                <ListButton />
              </OngoingParkingHeaderWrapper>
            )}
            {!isWeb && (
              <OngoingParkingHeaderWrapper>
                <CollapseButton />
              </OngoingParkingHeaderWrapper>
            )}
          </>
        </Animated.View>
        {(chargingOngoing || chargingStarting) && (
          <Animated.View
            style={collapsedSheetContainerStyle}
            testID="ongoing-charging-header-collapsed">
            <OngoingParkingHeaderWrapper mr={20}>
              <TerminateButton
                testID="ongoing-charging-stop-button"
                action={chargingStarting ? 'CANCEL' : 'STOP'}
                onPress={() =>
                  useUiStore
                    .getState()
                    .setSheetModal(
                      chargingStarting ? 'cancelCharging' : 'stopCharging'
                    )
                }
              />
            </OngoingParkingHeaderWrapper>
          </Animated.View>
        )}
        {chargingStopping && (
          <Animated.View
            style={collapsedSheetContainerStyle}
            testID="ongoing-charging-stopping">
            <LoadingSpinner isHeader={true} />
          </Animated.View>
        )}
      </OngoingSessionHeaderContainer>
    </HeaderContainer>
  );
};

export default OngoingChargingHeader;
